/* eslint-disable no-unused-vars */
/* global G */
import { asyncPipeSpread, setKey } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import { hide, show } from 'lib/sequence/component/state/hidden'

const EDIT = 30
const DELETE = 40
const DELETED_STATUS = 90

/**
 * Saves the long pressed message to the module state so that the edit/delete
 * button handlers (and the edit-mode aware `sendStatusMessage`) can access it.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const saveMessageToState = module => async (components, ...args) => {
  const { detail: message = {} } = args[0] || {}

  setKey(message, 'currentMessage', module[G.STATE])

  return [components, ...args]
}

/**
 * Shows or hides the edit and delete buttons depending on the message's ACL.
 *
 * Mirrors the gating web applies in its message template
 * (`platform/web/src/ui/Element/Message/List/Template/Message.jsx`): the edit
 * button requires ACL `30`, the delete button ACL `40`, and neither is shown
 * for an already deleted message.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayOptions = module => async (components, ...args) => {
  const { detail: message = {} } = args[0] || {}
  const { edit, delete: remove } = components

  const acl = message?.acl || []
  const isDeleted = message?.value?.status === DELETED_STATUS

  const canEdit = acl.includes(EDIT) && !isDeleted
  const canDelete = acl.includes(DELETE) && !isDeleted

  canEdit ? show(edit) : hide(edit)
  canDelete ? show(remove) : hide(remove)

  return [components, ...args]
}

/**
 * Message Options Action (mobile only)
 *
 * Renders the long press message menu holding the edit and delete buttons and
 * decides which of them to show based on the message's ACL.
 *
 * @param {Gaia.AppModule.Spec} module the request module composition object
 * @returns {function(*): function(...[*]): Promise<*[]>}
 */
export default module => component => async (...args) => await asyncPipeSpread(
  saveMessageToState(module),
  displayOptions(module),
)(find(component), ...args)
